import { useId, useMemo, useRef, useState, type JSX } from "react";
import {
  MERGE_CUSTOM_FIELD_PREFIX,
  MERGE_FIELD_KEYS,
  type DuplicateGroup,
  type DuplicatesResponse,
  type LeadCatalogResponse,
  type MergeCandidate,
  type MergeLeadsBody,
  type MergeLeadsResponse,
  type MergeValue,
} from "@gioia365/contracts";
import type { Locale } from "@gioia365/i18n";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Radio } from "../../design/components/Radio";
import { Reason } from "../../design/components/Reason";
import { Table } from "../../design/components/Table";
import { messageForError } from "../../i18n/errors";
import { formatCount, formatNumericDateTime } from "../../i18n/format";
import { useLocale, useT, type Translate } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { formatEuro, formatNumber } from "../card/card-fields";
import { leadCatalogQuery } from "../filters/filter-queries";
import { formatPhoneForDisplay, leadName } from "../lead-display";
import { duplicatesPagesQuery, useDismissPair, useMergeLeads } from "./cleanup-queries";
import type { CleanupOutcome } from "./DataCleanupDialog";

// «Doppioni (N)» (piano M3 Task 18, D11; mock-up `lead.html`, `renderClean` di `initLeadPage`): una coppia per volta, con il
// motivo (lo stesso cellulare o la stessa email), la tabella campo per campo con il Lead che resta a sinistra e l'altro a
// destra, dove i valori sono diversi una scelta (di partenza il Lead che resta) e dove sono uguali una riga sola, poi le
// chiamate dei due e i loro tag uniti. «Unisci in un solo Lead» manda le scelte con le versioni lette; «Non sono doppioni»
// toglie la coppia per tutta l'azienda. Se uno dei due Lead è cambiato nel frattempo (409, o il Lead che resta non è più lo
// stesso, o l'altro è già entrato in un terzo, o i due non sono più una coppia) lo si dice con una frase sola e la coppia si
// rilegge. Se la scheda oggi non accetta un valore scelto dell'altro Lead, la frase nomina il campo. Le azioni (`usePairActions`) e
// la coppia (`GroupView`) servono anche alla finestra dell'unione aperta dalla scheda del Lead (LEAD-28, `MergePairDialog`).

export type Side = "survivor" | "absorbed";
type CatalogField = LeadCatalogResponse["fields"][number];

/**
 * Un rifiuto che vuol dire «la coppia che hai davanti non è più quella»: si ricontrolla e si riprova. `not_duplicates`: nel
 * frattempo i due Lead non hanno più lo stesso cellulare o la stessa email, oppure qualcuno li ha segnati «Non sono doppioni»
 * (decisione P57); la coppia riletta non c'è più.
 */
const CHANGED_REASONS: ReadonlySet<unknown> = new Set(["survivor_rule", "absorbed_merged_elsewhere", "not_duplicates"]);

function pairChanged(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return false;
  return error.code === "version_conflict" || (error.code === "merge_invalid" && CHANGED_REASONS.has(error.details?.reason));
}

/** I campi della scheda come li nomina il server (`details.field` di `invalid_field_value`), con la chiave della tabella. */
const CARD_FIELD_KEYS: Readonly<Record<string, string>> = {
  firstName: "firstName",
  lastName: "lastName",
  salonName: "salonName",
  email: "email",
  cityIstatCode: "city",
  statusId: "status",
};
const CARD_CUSTOM_PREFIX = "custom.";

/**
 * Il campo della tabella il cui valore scelto dell'altro Lead la scheda oggi non accetta, oppure `null`. L'unione applica le
 * scelte con le regole della scheda, e un valore salvato tempo fa le può non passare più: uno status che non si sceglie più,
 * una città che non è nel catalogo, un nome importato troppo lungo, un fisso nel posto del cellulare, lo stesso numero in due
 * posti, un owner che non si può più assegnare. Si nomina soltanto un campo per cui si è scelto l'altro Lead.
 */
function refusedFieldOf(error: unknown, choices: Readonly<Record<string, Side>>): string | null {
  if (!(error instanceof ApiRequestError)) return null;
  const details = error.details ?? {};
  const phones = (["mobile", "landline"] as const).filter((key) => choices[key] === "absorbed");
  let key: string | null = null;
  if (error.code === "owner_not_eligible") key = "owner";
  else if ((error.code === "invalid_phone" || error.code === "phone_repeated_on_lead") && details.holder === "primary") {
    key = details.kind === "mobile" || details.kind === "landline" ? details.kind : null;
  } else if (error.code === "invalid_field_value" && typeof details.field === "string") {
    // Uno schema che rifiuta i telefoni non dice quale: lo si sa soltanto se dell'altro Lead se n'è scelto uno solo.
    if (details.field === "phones") key = phones.length === 1 ? (phones[0] ?? null) : null;
    else if (details.field.startsWith(CARD_CUSTOM_PREFIX)) key = `${MERGE_CUSTOM_FIELD_PREFIX}${details.field.slice(CARD_CUSTOM_PREFIX.length)}`;
    else key = CARD_FIELD_KEYS[details.field] ?? null;
  }
  return key !== null && choices[key] === "absorbed" ? key : null;
}

/** Le coppie di più pagine, una volta sola anche se una pagina riletta ne ha spostata una in quella dopo. */
function distinctGroups(pages: readonly DuplicatesResponse[]): DuplicateGroup[] {
  const seen = new Set<string>();
  const groups: DuplicateGroup[] = [];
  for (const group of pages.flatMap((page) => page.groups)) {
    if (seen.has(group.key)) continue;
    seen.add(group.key);
    groups.push(group);
  }
  return groups;
}

const SYSTEM_KEYS: ReadonlySet<string> = new Set(MERGE_FIELD_KEYS);

/**
 * I campi della tabella, nell'ordine della scheda: quelli di sistema che il server manda (la scheda li mostra), poi i campi
 * dell'azienda (`field:<chiave>`).
 */
function fieldKeysOf(group: DuplicateGroup): string[] {
  const all = [...Object.keys(group.survivor.values), ...Object.keys(group.absorbed.values)];
  const custom = [...new Set(all.filter((key) => key.startsWith(MERGE_CUSTOM_FIELD_PREFIX)))];
  return [...MERGE_FIELD_KEYS.filter((key) => all.includes(key)), ...custom];
}

const rawOf = (value: MergeValue | undefined) => (value?.raw === undefined || value.raw === "" ? null : value.raw);

export type ValueContext = { t: Translate; locale: Locale; catalog: ReadonlyMap<string, CatalogField> };

/** Il nome di un campo: dai cataloghi per quelli di sistema, dal catalogo dei campi dell'azienda per gli altri. */
function fieldName(ctx: ValueContext, key: string): string {
  if (SYSTEM_KEYS.has(key)) return ctx.t(`dataCleanup.fields.${key as (typeof MERGE_FIELD_KEYS)[number]}`);
  const fieldKey = key.slice(MERGE_CUSTOM_FIELD_PREFIX.length);
  return ctx.catalog.get(fieldKey)?.label ?? fieldKey;
}

/**
 * Un valore come lo si legge: «vuoto» per ogni valore che manca, owner compreso (mock-up, decisione P52), l'etichetta del
 * server, il telefono diviso, il numero o l'importo.
 */
function valueText(ctx: ValueContext, key: string, value: MergeValue | undefined): string {
  const { t, locale } = ctx;
  const raw = rawOf(value);
  if (raw === null) return t("dataCleanup.empty");
  const label = value?.label?.trim();
  if (label) return label;
  // Status, owner e città si leggono soltanto dall'etichetta: mai l'identificativo o il codice ISTAT.
  if (key === "status" || key === "owner" || key === "city") return "—";
  if (key === "mobile" || key === "landline") return formatPhoneForDisplay(String(raw));
  const field = key.startsWith(MERGE_CUSTOM_FIELD_PREFIX) ? ctx.catalog.get(key.slice(MERGE_CUSTOM_FIELD_PREFIX.length)) : undefined;
  if (typeof raw === "number") return field?.type === "currency_eur" ? formatEuro(locale, raw) : formatNumber(locale, raw);
  return field?.options.find((option) => option.id === raw)?.label ?? String(raw);
}

/** Il nome del Lead che resta con le scelte fatte: nome e cognome scelti, altrimenti il salone, altrimenti il telefono. */
function mergedName(group: DuplicateGroup, choices: Readonly<Record<string, Side>>): string {
  const chosen = (key: string) => {
    const side = choices[key] === "absorbed" ? group.absorbed : group.survivor;
    const raw = side.values[key]?.raw;
    return typeof raw === "string" ? raw.trim() : "";
  };
  if ("firstName" in group.survivor.values || "lastName" in group.survivor.values) {
    const name = [chosen("firstName"), chosen("lastName")].filter(Boolean).join(" ") || chosen("salonName");
    if (name) return name;
  }
  const mobile = chosen("mobile");
  return leadName({ displayName: group.survivor.displayName, primaryPhone: mobile || group.survivor.primaryPhone }) ?? "—";
}

/** L'esito dell'unione con ciò che è passato dall'altro Lead (`moved` del server): chiamate e note, se ce ne sono. */
function mergedText(t: Translate, locale: Locale, name: string, moved: MergeLeadsResponse["moved"]): string {
  const items: string[] = [];
  if (moved.calls > 0) items.push(moved.calls === 1 ? t("dataCleanup.movedCallsOne") : t("dataCleanup.movedCalls", { count: formatCount(locale, moved.calls) }));
  if (moved.notes > 0) items.push(moved.notes === 1 ? t("dataCleanup.movedNotesOne") : t("dataCleanup.movedNotes", { count: formatCount(locale, moved.notes) }));
  const text = t("dataCleanup.merged", { name });
  return items.length === 0 ? text : `${text}${t("dataCleanup.mergedMoved", { items: new Intl.ListFormat(locale, { type: "conjunction" }).format(items) })}`;
}

/**
 * «Unisci in un solo Lead» e «Non sono doppioni» di una coppia, con l'esito in una frase (`onOutcome`: riuscita o problema). Le due
 * azioni restituiscono la risposta dell'unione o `true` quando sono riuscite, `null` o `false` altrimenti. `keepGone` è quello di
 * `useMergeLeads`: dalla scheda del Lead.
 */
export function usePairActions(tab: TabContext, onOutcome: (outcome: CleanupOutcome | null) => void, options: { keepGone?: boolean } = {}) {
  const t = useT();
  const { locale } = useLocale();
  const catalog = useQuery(leadCatalogQuery(tab));
  const merge = useMergeLeads(tab, options);
  const dismiss = useDismissPair(tab);
  const busy = merge.isPending || dismiss.isPending;
  // Due clic nello stesso istante arrivano prima che `busy` diventi vero (lo stato della scrittura arriva al disegno dopo):
  // il secondo non parte (revisione finale, Minor 8).
  const acting = useRef(false);
  const catalogFields = useMemo(() => new Map((catalog.data?.fields ?? []).map((field) => [field.key, field])), [catalog.data]);
  const ctx: ValueContext = { t, locale, catalog: catalogFields };

  /** Il testo di un'unione rifiutata: la coppia cambiata, il valore che la scheda non accetta, oppure il testo dell'errore. */
  function refusalText(error: unknown, choices: Readonly<Record<string, Side>>): string {
    if (pairChanged(error)) return t("dataCleanup.conflict");
    const field = refusedFieldOf(error, choices);
    return field === null ? messageForError(t, error) : t("dataCleanup.valueRefused", { field: fieldName(ctx, field) });
  }

  async function mergeGroup(group: DuplicateGroup, choices: Record<string, Side>): Promise<MergeLeadsResponse | null> {
    if (busy || acting.current) return null;
    acting.current = true;
    onOutcome(null);
    const body: MergeLeadsBody = {
      survivorId: group.survivor.id,
      absorbedId: group.absorbed.id,
      expectedVersions: { survivor: group.survivor.version, absorbed: group.absorbed.version },
      choices,
    };
    try {
      const response = await merge.mutateAsync(body);
      onOutcome({ tone: "success", text: mergedText(t, locale, mergedName(group, choices), response.moved) });
      return response;
    } catch (error) {
      onOutcome({ tone: "warning", text: refusalText(error, choices) });
      return null;
    } finally {
      acting.current = false;
    }
  }

  async function dismissGroup(group: DuplicateGroup): Promise<boolean> {
    if (busy || acting.current) return false;
    acting.current = true;
    onOutcome(null);
    try {
      await dismiss.mutateAsync({ leadIds: [group.survivor.id, group.absorbed.id] });
      onOutcome({ tone: "success", text: t("dataCleanup.dismissed") });
      return true;
    } catch (error) {
      onOutcome({ tone: "warning", text: messageForError(t, error) });
      return false;
    } finally {
      acting.current = false;
    }
  }

  return { busy, ctx, mergeGroup, dismissGroup };
}

type Props = {
  tab: TabContext;
  view: string | null;
  timeZone: string;
  canChangeOwner: boolean;
  onOutcome: (outcome: CleanupOutcome | null) => void;
};

export function DuplicatesTab({ tab, view, timeZone, canChangeOwner, onOutcome }: Props): JSX.Element {
  const t = useT();
  const pages = useInfiniteQuery({ ...duplicatesPagesQuery(tab, view), placeholderData: keepPreviousData });
  const { busy, ctx, mergeGroup, dismissGroup } = usePairActions(tab, onOutcome);
  const groups = useMemo(() => distinctGroups(pages.data?.pages ?? []), [pages.data]);

  if (pages.isError && !pages.data) {
    return (
      <div className="g-cleanup__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, pages.error)}
        </Reason>
        <Button onClick={() => void pages.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  }
  if (!pages.data) return <p role="status">{t("common.loading")}</p>;
  if (groups.length === 0 && !pages.isPlaceholderData) return <p className="g-cleanup__empty">{t("dataCleanup.noDuplicates")}</p>;

  return (
    <div className="g-cleanup__groups" aria-busy={pages.isFetching ? "true" : undefined}>
      {groups.map((group) => (
        <GroupView
          // Una coppia riletta con versioni nuove (un Lead cambiato nel frattempo) riparte dalle scelte di partenza; così
          // anche quella in cui il Lead che resta è diventato l'altro, magari con le stesse versioni (`survivor_rule`: una
          // chiamata nuova), perché le scelte dicono un lato, e il lato adesso è dell'altro Lead.
          key={`${group.key}:${group.survivor.id}:${group.survivor.version}:${group.absorbed.version}`}
          group={group}
          ctx={ctx}
          timeZone={timeZone}
          busy={busy}
          canChangeOwner={canChangeOwner}
          onMerge={(choices) => void mergeGroup(group, choices)}
          onDismiss={() => void dismissGroup(group)}
        />
      ))}
      {pages.hasNextPage ? (
        <div>
          <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage} aria-busy={pages.isFetchingNextPage ? "true" : undefined}>
            {t("dataCleanup.more")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

type GroupProps = {
  group: DuplicateGroup;
  ctx: ValueContext;
  timeZone: string;
  busy: boolean;
  canChangeOwner: boolean;
  onMerge: (choices: Record<string, Side>) => void;
  onDismiss: () => void;
};

/** L'intestazione della colonna di un Lead: quando l'ha chiamato qualcuno l'ultima volta (o quando è entrato) e il suo nome. */
function LeadHead({ text, lead, t }: { text: string; lead: MergeCandidate; t: Translate }) {
  return (
    <th scope="col">
      <span className="g-merge__when">{text}</span>
      <Link className="g-merge__lead" to="/lead/$leadId" params={{ leadId: lead.id }}>
        {leadName(lead) ?? t("dataCleanup.openLead")}
      </Link>
    </th>
  );
}

export function GroupView({ group, ctx, timeZone, busy, canChangeOwner, onMerge, onDismiss }: GroupProps): JSX.Element {
  const { t, locale } = ctx;
  const headingId = useId();
  const ownerReasonId = useId();
  const choiceId = useId();
  const [picked, setPicked] = useState<Readonly<Record<string, Side>>>({});
  const { survivor, absorbed } = group;

  const rows = fieldKeysOf(group).map((key) => {
    const kept = survivor.values[key];
    const other = absorbed.values[key];
    return { key, kept, other, equal: rawOf(kept) === rawOf(other) };
  });
  // Le scelte che si mandano: una per ogni campo con valori diversi, di partenza il Lead che resta.
  const choices: Record<string, Side> = Object.fromEntries(rows.filter((row) => !row.equal).map((row) => [row.key, picked[row.key] ?? "survivor"]));
  const reason = group.reason === "phone" ? t("dataCleanup.samePhone", { value: formatPhoneForDisplay(group.value) }) : t("dataCleanup.sameEmail", { value: group.value });
  const staysHead = survivor.latestCallAt
    ? t("dataCleanup.columns.stays", { when: formatNumericDateTime(timeZone, survivor.latestCallAt) })
    : t("dataCleanup.columns.staysNoCall", { when: formatNumericDateTime(timeZone, survivor.createdAt) });
  const otherHead = absorbed.latestCallAt
    ? t("dataCleanup.columns.other", { when: formatNumericDateTime(timeZone, absorbed.latestCallAt) })
    : t("dataCleanup.columns.otherNoCall", { when: formatNumericDateTime(timeZone, absorbed.createdAt) });
  // I tag dei due, senza doppioni: prima quelli del Lead che resta.
  const tagNames = [...new Map([...survivor.tags, ...absorbed.tags].map((tag) => [tag.id, tag.name])).values()];

  function choice(key: string, side: Side, value: MergeValue | undefined, locked: boolean) {
    return (
      <Radio
        id={`${choiceId}-${key}-${side}`}
        className="g-merge__choice"
        name={`merge-${group.key}-${key}`}
        value={side}
        label={valueText(ctx, key, value)}
        checked={choices[key] === side}
        disabled={busy || locked}
        describedBy={locked ? ownerReasonId : undefined}
        onChange={() => setPicked((current) => ({ ...current, [key]: side }))}
      />
    );
  }

  return (
    <article className="g-merge" aria-labelledby={headingId}>
      <h3 id={headingId} className="g-merge__reason">
        {reason}
      </h3>
      <Table className="g-merge__table" caption={t("dataCleanup.caption")} busy={busy}>
          <thead>
            <tr>
              <th scope="col">{t("dataCleanup.columns.field")}</th>
              <LeadHead text={staysHead} lead={survivor} t={t} />
              <LeadHead text={otherHead} lead={absorbed} t={t} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              // Tenere l'owner dell'altro Lead cambia l'owner: senza la funzione il server rifiuterebbe tutta l'unione.
              const locked = row.key === "owner" && !canChangeOwner;
              return (
                <tr key={row.key}>
                  <th scope="row">{fieldName(ctx, row.key)}</th>
                  {row.equal ? (
                    <td colSpan={2}>{valueText(ctx, row.key, row.kept)}</td>
                  ) : (
                    <>
                      <td>{choice(row.key, "survivor", row.kept, false)}</td>
                      <td>
                        {choice(row.key, "absorbed", row.other, locked)}
                        {locked ? (
                          <Reason id={ownerReasonId} className="g-merge__locked">
                            {t("dataCleanup.ownerNeedsFeature")}
                          </Reason>
                        ) : null}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            <tr>
              <th scope="row">{t("dataCleanup.callsRow")}</th>
              <td colSpan={2}>{t("dataCleanup.callsText", { a: formatCount(locale, survivor.callCount), b: formatCount(locale, absorbed.callCount) })}</td>
            </tr>
            <tr>
              <th scope="row">{t("dataCleanup.tagsRow")}</th>
              <td colSpan={2}>{tagNames.length > 0 ? tagNames.join(", ") : t("dataCleanup.noTags")}</td>
            </tr>
          </tbody>
      </Table>
      <div className="g-merge__actions">
        <Button variant="primary" onClick={() => onMerge(choices)} disabled={busy}>
          {t("dataCleanup.merge")}
        </Button>
        <Button onClick={onDismiss} disabled={busy}>
          {t("dataCleanup.notDuplicates")}
        </Button>
      </div>
    </article>
  );
}
